import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { ModalForm, type FormField } from '@/components/ui/modal-form';
import {
  useRequisitions,
  useApproveRequisition,
  useRejectRequisition,
  useRequestRevisionRequisition,
  useDelegateApproval,
} from '@/hooks/useRequisitions';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import type { Requisition } from '@/lib/schemas';
import { formatCurrency, cn } from '@/lib/utils';
import {
  CheckCircle,
  XCircle,
  MessageSquare,
  Share2,
  User,
  Calendar,
  AlertTriangle,
  Clock,
  Filter,
  Inbox,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const approvalActionLabels = {
  approve: 'Approve',
  reject: 'Reject',
  revision: 'Request Revision',
  delegate: 'Delegate',
};

export default function ApprovalsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [selectedRequisition, setSelectedRequisition] = useState<Requisition | null>(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | 'revision' | 'delegate' | null>(null);
  const [filterBy, setFilterBy] = useState<'all' | 'pending' | 'actioned' | 'urgent'>('all');
  const [showMyApprovalsOnly, setShowMyApprovalsOnly] = useState(false);

  const { data: requisitionsResponse, isLoading, error } = useRequisitions();
  const requisitions = requisitionsResponse?.data || [];
  const approveMutation = useApproveRequisition();
  const rejectMutation = useRejectRequisition();
  const revisionMutation = useRequestRevisionRequisition();
  const delegateMutation = useDelegateApproval();

  // Check if a requisition is urgent (required date within 3 days)
  const isUrgent = (req: Requisition) => {
    return req.required_date && 
      new Date(req.required_date) < new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
  };

  // Check if user can action this requisition (based on approval chain)
  const canUserAction = (req: Requisition) => {
    // In production, this would check if current user's role is in the current approval level
    // For demo, we'll check if there's a pending approval in the chain
    return req.approval_chain?.some(a => a.status === 'pending');
  };

  // Get requisitions that are submitted and have pending approvals
  const pendingApprovals = useMemo(() => {
    let filtered = requisitions.filter((req) => 
      req.status === 'submitted' && req.approval_chain?.some(a => a.status === 'pending')
    );

    // Apply filter
    switch (filterBy) {
      case 'pending':
        filtered = filtered.filter(req => !req.approval_chain?.some(a => a.status !== 'pending'));
        break;
      case 'actioned':
        filtered = filtered.filter(req => req.approval_chain?.some(a => a.status !== 'pending'));
        break;
      case 'urgent':
        filtered = filtered.filter(isUrgent);
        break;
    }

    // Apply user filter
    if (showMyApprovalsOnly) {
      filtered = filtered.filter(canUserAction);
    }

    // Sort by urgency first, then by date
    return filtered.sort((a, b) => {
      if (isUrgent(a) && !isUrgent(b)) return -1;
      if (!isUrgent(a) && isUrgent(b)) return 1;
      if (a.required_date && b.required_date) {
        return new Date(a.required_date).getTime() - new Date(b.required_date).getTime();
      }
      return 0;
    });
  }, [requisitions, filterBy, showMyApprovalsOnly]);

  const openApprovalAction = (requisition: Requisition, action: 'approve' | 'reject' | 'revision' | 'delegate') => {
    setSelectedRequisition(requisition);
    setApprovalAction(action);
    setIsActionModalOpen(true);
  };

  const submitApprovalAction = async (data: { comments?: string }) => {
    if (!selectedRequisition?.requisition_id || !approvalAction) return;

    const approverId = user?.id || 'current-user'; // Would come from auth context

    try {
      switch (approvalAction) {
        case 'approve':
          await approveMutation.mutateAsync({
            id: selectedRequisition.requisition_id,
            approverId,
            comments: data.comments,
          });
          toast.success('Requisition approved', `Requisition ${selectedRequisition.requisition_number} has been approved.`);
          break;
        case 'reject':
          await rejectMutation.mutateAsync({
            id: selectedRequisition.requisition_id,
            approverId,
            comments: data.comments || 'Rejected',
          });
          toast.warning('Requisition rejected', `Requisition ${selectedRequisition.requisition_number} has been rejected.`);
          break;
        case 'revision':
          await revisionMutation.mutateAsync({
            id: selectedRequisition.requisition_id,
            approverId,
            comments: data.comments || 'Revision requested',
          });
          toast.info('Revision requested', `Requisition ${selectedRequisition.requisition_number} has been sent back for revision.`);
          break;
        case 'delegate':
          await delegateMutation.mutateAsync({
            id: selectedRequisition.requisition_id,
            approverId,
            delegateToId: 'delegate-user-1',
          });
          toast.success('Approval delegated', `Requisition ${selectedRequisition.requisition_number} has been delegated.`);
          break;
      }
      setIsActionModalOpen(false);
      setSelectedRequisition(null);
      setApprovalAction(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error('Action failed', message);
      console.error('Approval action failed:', error);
    }
  };

  const approvalFields: FormField[] = [
    {
      name: 'comments',
      label: 'Comments',
      type: 'textarea',
      placeholder: 'Add comments (required for reject/revision)',
      helperText: 'Comments help the requestor understand your decision',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Approvals
          </h1>
          <p className="text-text-secondary mt-1">
            Review and process pending approval requests
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="default" className="px-3 py-1">
            {pendingApprovals.length} Pending
          </Badge>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="p-4 rounded-lg bg-red-50 border-2 border-red-200 flex items-start gap-3"
          role="alert"
          aria-live="assertive"
        >
          <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-red-900">Error loading approvals</p>
            <p className="text-sm text-red-800 mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className={cn('cursor-pointer transition-all', filterBy === 'all' && 'ring-2 ring-primary')}
          onClick={() => setFilterBy('all')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-muted">All Pending</p>
                <p className="text-2xl font-bold">{pendingApprovals.length}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Inbox className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={cn('cursor-pointer transition-all', filterBy === 'urgent' && 'ring-2 ring-red-500')}
          onClick={() => setFilterBy('urgent')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-muted">Urgent</p>
                <p className="text-2xl font-bold text-red-600">
                  {requisitions.filter(r => r.status === 'submitted' && isUrgent(r)).length}
                </p>
              </div>
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer transition-all" onClick={() => setFilterBy('pending')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-muted">Awaiting Action</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {requisitions.filter(r => r.status === 'submitted').length}
                </p>
              </div>
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer transition-all">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-muted">My Approvals</p>
                <p className="text-2xl font-bold text-green-600">
                  {requisitions.filter(r => r.status === 'submitted' && canUserAction(r)).length}
                </p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <User className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-text-muted" />
              <span className="text-sm font-medium text-text-secondary">Filters:</span>
            </div>
            <Select value={filterBy} onValueChange={(v: 'all' | 'pending' | 'actioned' | 'urgent') => setFilterBy(v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pending</SelectItem>
                <SelectItem value="pending">Awaiting Action</SelectItem>
                <SelectItem value="urgent">Urgent Only</SelectItem>
                <SelectItem value="actioned">Partially Reviewed</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={showMyApprovalsOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowMyApprovalsOnly(!showMyApprovalsOnly)}
            >
              <User className="mr-2 h-4 w-4" />
              My Approvals Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Approvals table */}
      {isLoading ? (
        <TableSkeleton rows={10} columns={5} />
      ) : pendingApprovals.length > 0 ? (
        <DataTable
          columns={[
            {
              key: 'requisition',
              header: 'Requisition',
              render: (req: Requisition) => (
                <div>
                  <p className="font-medium text-text-primary">
                    {req.requisition_number || 'Draft'}
                  </p>
                  <p className="text-xs text-text-muted flex items-center gap-1 mt-1">
                    <User className="h-3 w-3" />
                    {req.requested_by}
                  </p>
                </div>
              ),
            },
            {
              key: 'items',
              header: 'Items',
              render: (req: Requisition) => (
                <div className="text-sm">
                  <p className="font-medium">{req.items.length} item(s)</p>
                  <p className="text-text-muted text-xs">
                    {formatCurrency(
                      req.items.reduce(
                        (sum, item) => sum + (item.quantity_requested * (item.unit_cost || 0)),
                        0
                      )
                    )}
                  </p>
                </div>
              ),
            },
            {
              key: 'approval_step',
              header: 'Step',
              render: (req: Requisition) => {
                const nextPending = req.approval_chain?.find(a => a.status === 'pending');
                return (
                  <Badge variant="default">
                    {nextPending?.role.replace('_', ' ') || 'Unknown'}
                  </Badge>
                );
              },
            },
            {
              key: 'deadline',
              header: 'Deadline',
              render: (req: Requisition) => {
                const nextPending = req.approval_chain?.find(a => a.status === 'pending');
                return nextPending?.deadline ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-text-muted" />
                    <span>{new Date(nextPending.deadline).toLocaleDateString()}</span>
                  </div>
                ) : (
                  <span className="text-text-muted">N/A</span>
                );
              },
            },
            {
              key: 'actions',
              header: 'Actions',
              render: (req: Requisition) => (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => openApprovalAction(req, 'approve')}
                    className="gap-1"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => openApprovalAction(req, 'reject')}
                    className="gap-1"
                  >
                    <XCircle className="h-4 w-4" />
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openApprovalAction(req, 'revision')}
                    className="gap-1"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Revision
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openApprovalAction(req, 'delegate')}
                    className="gap-1"
                  >
                    <Share2 className="h-4 w-4" />
                    Delegate
                  </Button>
                </div>
              ),
            },
          ]}
          data={pendingApprovals}
          loading={false}
          getItemId={(req) => req.requisition_id!}
        />
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-text-secondary">No pending approvals</p>
            <p className="text-sm text-text-muted mt-1">
              You're all caught up! Check back later for new requests.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Approval Action Modal */}
      <ModalForm
        open={isActionModalOpen}
        onOpenChange={setIsActionModalOpen}
        title={`${approvalAction ? approvalActionLabels[approvalAction] : ''} Requisition`}
        description={`You are about to ${approvalAction} requisition ${selectedRequisition?.requisition_number}`}
        fields={approvalFields}
        onSubmit={submitApprovalAction}
        isLoading={approveMutation.isPending || rejectMutation.isPending || revisionMutation.isPending || delegateMutation.isPending}
      />
    </div>
  );
}
